import { SelectProps } from "@amzn/awsui-components-react";
import {
    CustomFormFieldTypes,
    CustomFormFieldVariants,
    FormFieldWrapperProps,
    InputDefaultProps,
} from "./CustomFormFieldProps";
import { SetStateAction } from "react";

/**
 * Represent a format to auto generate a custom form field using the CustomForm component
 *
 * @interface
 * @property {string} id - Unique identifier for the form field
 * @property {CustomFormFieldVariants} variant - The type of form field to render (input, select, toggle, etc.)
 * @property {CustomFormFieldTypes} [defaultValue] - Optional default value for the field
 * @property {SelectProps.Options} [options] - Options for select-type form field
 * @property {boolean} [isVisible] - Whether the form field should be visible
 * @property {string} [testId] - The test id used for the form field
 * @property {number} rows = The rows size for the text area
 * @property {string} accept - The file accept types
 * @property {boolean} multiple = The file dropzone accepts multiple files
 * @property {function} validateInput - Optional validation callback for array-input that returns an error message or undefined. Receives the input and current array values for duplicate checking.
 * @property {function} sanitizeInput - Optional sanitization callback for array-input that transforms input before adding
 */
export interface CustomFormField extends FormFieldWrapperProps, InputDefaultProps {
    id: string;
    variant: CustomFormFieldVariants;
    defaultValue?: CustomFormFieldTypes;
    options?: SelectProps.Options;
    isVisible?: boolean;
    testId?: string;
    rows?: number;
    accept?: string;
    multiple?: boolean;
    validateInput?: (input: string, currentValues: string[]) => string | undefined;
    sanitizeInput?: (input: string) => string;
}

/**
 * Represents a group of form fields that can be displayed together with an optional header.
 *
 * @interface
 * @property {React.ReactNode} [header] - Optional header for the form section
 * @property {CustomFormField[]} formInputs - Array of form fields to be included in this section
 */
export interface CustomFormFieldGroup {
    header?: React.ReactNode;
    formInputs: CustomFormField[];
}

/**
 * Represent the props for the CustomForm component
 *
 * @interface
 * @property {React.ReactNode} actions - Form actions buttons
 * @property {React.ReactNode} header - Form header content
 * @property {CustomFormFieldGroup[]} formSections - Array of form sections, each can contain multiple form fields
 * @property {Map<string, CustomFormFieldTypes>} formValues - Current values of all form fields, mapped by field ID
 * @property {React.Dispatch<SetStateAction<Map<string, CustomFormFieldTypes>>>} setFormValues - State setter function to update form values
 * @property {React.FormEventHandler<HTMLFormElement>} onSubmit - Form submission handler
 */
export interface CustomFormProps {
    actions: React.ReactNode;
    header: React.ReactNode;
    formSections: CustomFormFieldGroup[];
    formValues: Map<string, CustomFormFieldTypes>;
    setFormValues: React.Dispatch<SetStateAction<Map<string, CustomFormFieldTypes>>>;
    onSubmit: React.FormEventHandler<HTMLFormElement>;
}

/**
 * Maps each form field variant to its corresponding default value type.
 * Used to provide appropriate default values based on the field type.
 */
type FieldDefaultValueMap = {
    [key in CustomFormFieldVariants]: CustomFormFieldTypes;
};

/**
 * Default values for each type of form field.
 * These values are used when no specific default is provided for a field.
 */
export const CUSTOM_FORM_DEFAULT_VALUES: FieldDefaultValueMap = {
    toggle: false,
    date: "",
    select: null,
    fileDropzone: [],
    input: "",
    textArea: "",
    "date-picker": "",
    "array-input": [],
};
